import { useLayoutEffect, useState, type ReactNode } from 'react';
import { PostHogProvider } from '@posthog/react';

import { initAnalytics, setContextGetter } from './browser';
import type { InitOptions } from './profiles';

export type AnalyticsProviderProps = InitOptions & { children: ReactNode };

/**
 * Initializes analytics from the props each app passes in, then exposes the
 * posthog client through `@posthog/react`'s context (live mode only). In
 * debug/off mode there is no client, so children render unwrapped.
 *
 * Init runs once per page (module guard in `initAnalytics`): later renders
 * and remounts reuse the first client. `context` is read through a ref, so a
 * new function each render never re-inits and every event sees the latest one.
 *
 * Mount inside the app's auth provider (e.g. Clerk) so identify can read the
 * session, and wrapping the router.
 */
export function AnalyticsProvider({ children, ...options }: AnalyticsProviderProps) {
  const [client] = useState(() => initAnalytics(options));
  const { context } = options;

  useLayoutEffect(() => {
    if (context) setContextGetter(context);
  }, [context]);

  if (!client) {
    return <>{children}</>;
  }

  return <PostHogProvider client={client}>{children}</PostHogProvider>;
}
